import type { Doc } from '@ppwr/convex/_generated/dataModel'
import { uniqBy } from 'lodash'

type PotWinners = NonNullable<Doc<'handHistory'>['potDistribution']>['potWinners']
type PotWinner = PotWinners[0]
type InnerWinners = NonNullable<PotWinner>['winners']
export type InnerWinner = InnerWinners[0]

type Winners = Doc<'handHistory'>['winners']
type Winner = Winners[0]

const buildWinnerMessage = (winner: Winner, noAmount: boolean) => {
  if (!winner) return 'No winner'
  const { playerName, amount, handDescription } = winner
  const amountStr = noAmount ? '' : `, ${amount}`
  const str = `${playerName}${amountStr}, ${handDescription}`
  return str
}

// Hand # | Pot: 1600 | Winners: Bot 1, 800, Pair of A's; Bot 2, 800, Pair of A's
export const buildWinnersDistributionMessage = (hand: Doc<'handHistory'> | undefined) => {
  const { handNumber, pot, winners } = hand || {}
  if (!(hand && winners?.length)) {
    return 'No winners for this hand.'
  }

  let str = ''
  winners.forEach((winner: Winner, index: number) => {
    str += buildWinnerMessage(winner, winners?.length === 1)
    if (index < winners.length - 1) {
      str += '; '
    }
  })

  const winnerStr = `Winner${1 < winners.length ? 's' : ''}:`
  const start = `Hand ${handNumber} | Pot: ${pot} | ${winnerStr} `

  // str +=
  //   ' really really long message to test how the chat message component handles overflow and wrapping of text within the chat bubble. This should give us a good idea of whether the UI remains intact and user-friendly even when faced with unusually lengthy messages that might occur in real-world usage scenarios.'

  return start + str
}

const buildPotWinnerMessage = (winner: InnerWinner, noAmount: boolean) => {
  if (!winner) {
    return 'No winner'
  }

  const {
    playerName,
    amount,
    hand: { cards, description, rank } = {}
  } = winner
  const amountStr = noAmount ? '' : `, ${amount}`
  const str = `${playerName}${amountStr}, ${description}`
  return str
}

// Hand # | Pot: 1600 | Winners: Bot 1, 800, Pair of A's; Bot 2, 800, Pair of A's
export const buildPotDistributionMessage = (hand: Doc<'handHistory'> | undefined) => {
  const { handNumber, pot, potDistribution, winners } = hand || {}
  if (!(hand && potDistribution?.potWinners?.length)) {
    return 'No winners for this hand.'
  }

  const { potWinners, totalAwarded } = potDistribution
  let str = ''
  let winnerCount = 0 // Count nested winners

  potWinners.forEach((potWinner: PotWinner, index: number) => {
    potWinner.winners.forEach((winner: InnerWinner, wIndex: number) => {
      winnerCount += 1
      str += buildPotWinnerMessage(winner, winners?.length === 1)
      if (wIndex < potWinner.winners.length - 1) {
        str += '; '
      }
    })
    if (index < potWinners.length - 1) {
      str += ' | '
    }
  })

  const winnerStr = `Winner${1 < winnerCount ? 's' : ''}:`
  const start = `Hand ${handNumber} | Pot: ${totalAwarded} | ${winnerStr} `

  // str +=
  //   ' really really long message to test how the chat message component handles overflow and wrapping of text within the chat bubble. This should give us a good idea of whether the UI remains intact and user-friendly even when faced with unusually lengthy messages that might occur in real-world usage scenarios.'

  return start + str
}

export const getPotDistributionWinners = (hand: Doc<'handHistory'> | undefined, makeUnique = false) => {
  const { handNumber, pot, potDistribution, winners } = hand || {}
  if (!(hand && potDistribution?.potWinners?.length)) {
    return []
  }

  let winnersList: InnerWinner[] = []
  const { potWinners } = potDistribution
  let winnerCount = 0

  potWinners?.forEach((potWinner: PotWinner) => {
    potWinner.winners.forEach((winner: InnerWinner) => {
      winnerCount += 1
      winnersList.push(winner)
    })
  })

  if (makeUnique) {
    // Users who won multiple pots only show once
    winnersList = uniqBy(winnersList, 'userId')
  }

  return winnersList
}
